/**
 * SkyViewLut — Hillaire 2020 sky-view LUT.
 *
 * The sky-view LUT is the per-frame product the background node samples
 * directly. It stores, for every view direction (encoded as
 * azimuth-to-sun × non-linear zenith via `skyViewUV`), the full
 * single-scattering + multiple-scattering radiance of the atmosphere as seen
 * from the camera's current altitude with the sun at its current position.
 *
 * Size: 192×108 (Hillaire's canonical lat-long-ish layout). The zenith axis
 * is warped toward the horizon (sqrt remap, two halves) so the steep colour
 * gradient at the horizon is well-resolved with few texels — see
 * `skyViewUV` in `../lutParameterization.ts`.
 *
 * Each texel ray-marches `steps` segments from the camera through the
 * atmosphere (Hillaire §6). At each sample it adds:
 *   - single scattering:   σ_s · phase(θ_VL) · T_sun  · throughput
 *   - multiple scattering:  σ_s · Ψ_ms(r, µ_s)         · throughput
 * where T_sun is read from the transmittance LUT and Ψ_ms from the
 * multi-scatter LUT. The view throughput accumulates the segment
 * transmittance. The sun disc is NOT baked here — it is added analytically
 * in the background node so it stays crisp regardless of LUT resolution.
 *
 * Unlike the transmittance/multi-scatter LUTs (which depend only on the
 * atmosphere config), the sky-view LUT depends on sun direction AND camera
 * altitude, so it is re-baked when EITHER changes beyond a threshold (see
 * SkyLutSet dirty-flagging) — not every frame.
 *
 * Reference: Hillaire 2020, §6 (Sky-View LUT) + eqs. (3),(4).
 */

import type * as THREE from 'three/webgpu';
import type { AtmosphereConfig, Vec3 } from '../AtmosphereConfig.js';
import {
  computeDensities, rayleighPhase, cornetteShanksPhase,
} from '../AtmosphereConfig.js';
import {
  skyViewUVInverse, rayAtmosphereIntersect,
} from '../lutParameterization.js';
import { transmittanceUV } from '../lutParameterization.js';
import { multiScatterUV } from './MultiScatterLut.js';
import {
  makeFloatTexture, sampleTransmittanceLut, clamp01, rayNearIntersect, earthShadowCpu, lerp,
} from './TransmittanceLut.js';
import { sampleMultiScatterLut } from './MultiScatterLut.js';

/** Canonical Hillaire sky-view LUT size. */
export const SKYVIEW_LUT_WIDTH = 192;
export const SKYVIEW_LUT_HEIGHT = 108;

/** Ray-march steps per sky-view texel. */
export const SKYVIEW_MARCH_STEPS = 32;

export interface SkyViewContext {
  cfg: AtmosphereConfig;
  /** Camera radius from planet centre (km). */
  viewHeight: number;
  /** Sun zenith cosine in the camera's local up-frame. */
  cosSunZenith: number;
  /** Baked transmittance LUT. */
  transmittanceData: Float32Array;
  transmittanceWidth: number;
  transmittanceHeight: number;
  /** Baked multi-scatter LUT (square, side = size). */
  multiScatterData: Float32Array;
  multiScatterSize: number;
  /** Sun illuminance multiplier. */
  sunIlluminance: number;
}

/**
 * CPU reference: single + multiple scattering radiance for one view
 * direction, given its zenith cosine (µ = dot(V, up)) and the cosine of the
 * angle between the view ray and the sun (cosVL = dot(V, L)).
 *
 * The sun's local-frame direction is L = (sqrt(1−c²), c, 0) with
 * c = cosSunZenith; we derive the per-sample sun-zenith cosine for the LUT
 * lookups from the geometry of V and L (flat-frame approximation: the local
 * "up" stays constant along the short atmospheric path, as in Hillaire's
 * sky-view LUT).
 */
/**
 * THE LOCAL SUN (2026-10-06): the sun's zenith cosine at a sample `t` km along a view ray — dot(sunDir, P)/|P| with
 * P = r0·up + t·view — is (r0·μs + t·ν)/r (Bruneton), ν = dot(view, sun). Never the camera's μs at every sample: that
 * flattens the planet, so after sunset the air toward the sun is as shadowed as the air away from it (no twilight arch,
 * no Earth's shadow). Clamped to [−1, 1].
 */
export function localSunCos(r0: number, cosSunZenith: number, t: number, cosLightView: number, rAtSample: number): number {
  const c = (r0 * cosSunZenith + t * cosLightView) / rAtSample;
  return c > 1 ? 1 : c < -1 ? -1 : c;
}

export function evaluateSkyViewRadiance(
  ctx: SkyViewContext,
  cosViewZenith: number,
  cosLightView: number,
): Vec3 {
  const { cfg } = ctx;
  const r0 = ctx.viewHeight;
  const mu = cosViewZenith;

  // Ray length: to top of atmosphere, or to the ground if it intersects.
  // The ground test uses the NEAREST surface root (rayNearIntersect) — the
  // far root would march the ray through the planet and sample sub-surface
  // densities that overflow the exponential extinction.
  const tTop = rayAtmosphereIntersect(r0, mu, cfg.topRadius);
  const tGround = rayNearIntersect(r0, mu, cfg.bottomRadius);
  let tMax = tTop;
  let hitGround = false;
  if (tGround > 0 && tGround < tTop) {
    tMax = tGround;
    hitGround = true;
  }
  if (tMax <= 0) return { x: 0, y: 0, z: 0 };

  // Phase functions depend on the scatter angle θ_VL.
  const phaseR = rayleighPhase(cosLightView);
  const phaseM = cornetteShanksPhase(cosLightView, cfg.miePhaseG);

  const steps = SKYVIEW_MARCH_STEPS;
  const dt = tMax / steps;

  let lx = 0, ly = 0, lz = 0;     // accumulated radiance
  let trX = 1, trY = 1, trZ = 1;  // view throughput

  for (let s = 0; s < steps; s++) {
    const t = (s + 0.5) * dt;
    const rAtSample = Math.sqrt(r0 * r0 + 2.0 * r0 * mu * t + t * t);
    const altitudeKm = Math.max(0, rAtSample - cfg.bottomRadius);
    const d = computeDensities(cfg, altitudeKm);

    // Scattering coefficients at the sample.
    const sigSrX = cfg.rayleighScattering.x * d.rayleigh;
    const sigSrY = cfg.rayleighScattering.y * d.rayleigh;
    const sigSrZ = cfg.rayleighScattering.z * d.rayleigh;
    const sigSmX = cfg.mieScattering.x * d.mie;
    const sigSmY = cfg.mieScattering.y * d.mie;
    const sigSmZ = cfg.mieScattering.z * d.mie;

    // Total extinction for the view throughput.
    const extX = cfg.rayleighScattering.x * d.rayleigh
      + cfg.mieExtinction.x * d.mie + cfg.ozoneExtinction.x * d.ozone;
    const extY = cfg.rayleighScattering.y * d.rayleigh
      + cfg.mieExtinction.y * d.mie + cfg.ozoneExtinction.y * d.ozone;
    const extZ = cfg.rayleighScattering.z * d.rayleigh
      + cfg.mieExtinction.z * d.mie + cfg.ozoneExtinction.z * d.ozone;

    // Sun transmittance from this sample — THE LOCAL SUN: the sun's zenith angle AT the sample (the camera's at every
    // sample flattened the planet: no twilight arch, no Earth's shadow after sunset).
    const muS = localSunCos(r0, ctx.cosSunZenith, t, cosLightView, rAtSample);
    const tUV = transmittanceUV(cfg, rAtSample, muS);
    const sunT0 = sampleTransmittanceLut(
      ctx.transmittanceData, ctx.transmittanceWidth, ctx.transmittanceHeight,
      tUV.u, tUV.v,
    );
    // The planet's shadow: a sample the Earth hides from the sun gets no single scatter (earthShadowCpu's WHY).
    const shadow = earthShadowCpu(cfg.bottomRadius, rAtSample, muS);
    const sunT = { x: sunT0.x * shadow, y: sunT0.y * shadow, z: sunT0.z * shadow };

    // Multiple-scattering ambient term Ψ_ms.
    const msUV = multiScatterUV(cfg, rAtSample, muS);
    const psi = sampleMultiScatterLut(
      ctx.multiScatterData, ctx.multiScatterSize, msUV.u, msUV.v,
    );

    // In-scatter: single (phase·T_sun) + multi (isotropic Ψ_ms).
    const inX = (sigSrX * phaseR + sigSmX * phaseM) * sunT.x + (sigSrX + sigSmX) * psi.x;
    const inY = (sigSrY * phaseR + sigSmY * phaseM) * sunT.y + (sigSrY + sigSmY) * psi.y;
    const inZ = (sigSrZ * phaseR + sigSmZ * phaseM) * sunT.z + (sigSrZ + sigSmZ) * psi.z;

    // Energy-conserving integration of in-scatter over the segment
    // (Hillaire §6: analytic per-segment integral of S·T over dt). We use
    // the simpler midpoint accumulation S·throughput·dt which matches the
    // v1 inline march for cross-validation.
    lx += trX * inX * dt;
    ly += trY * inY * dt;
    lz += trZ * inZ * dt;

    trX *= Math.exp(-extX * dt);
    trY *= Math.exp(-extY * dt);
    trZ *= Math.exp(-extZ * dt);
  }

  // Optional ground contribution (bounced sunlight) when the ray hits the
  // surface — Lambertian albedo × sun transmittance × N·L. Kept simple.
  if (hitGround) {
    const groundR = cfg.bottomRadius;
    const muG = localSunCos(r0, ctx.cosSunZenith, tMax, cosLightView, groundR); // THE LOCAL SUN at the ground hit
    const tUV = transmittanceUV(cfg, groundR, muG);
    const sunT = sampleTransmittanceLut(
      ctx.transmittanceData, ctx.transmittanceWidth, ctx.transmittanceHeight,
      tUV.u, tUV.v,
    );
    const nDotL = Math.max(0, muG);
    const a = cfg.groundAlbedo;
    lx += trX * sunT.x * a.x * nDotL / Math.PI;
    ly += trY * sunT.y * a.y * nDotL / Math.PI;
    lz += trZ * sunT.z * a.z * nDotL / Math.PI;
  }

  return { x: lx * ctx.sunIlluminance, y: ly * ctx.sunIlluminance, z: lz * ctx.sunIlluminance };
}

export interface SkyViewLutResult {
  data: Float32Array;
  width: number;
  height: number;
  texture: THREE.DataTexture;
}

/**
 * The row half of `evaluateSkyViewRadiance` (shader sweep 2026-10-06 — the boot profile's SkyViewLut hot spot): a
 * sky-view ROW is one view-zenith cosine, and what the march samples along that ray — radius, densities, the view
 * throughput — is independent of the COLUMN (the azimuth to the sun). THE LOCAL SUN (2026-10-06) made the sun's
 * transmittance, shadow and Ψ_ms depend on the column (they read the sun's angle AT the sample, which moves with the
 * azimuth: (r0·μs + t·ν)/r), so those three lookups run per column — but everything in them that depends only on the
 * sample's radius is hoisted into the row: the transmittance mapping's d_min / d_max, the shadow's c, and each LUT's two
 * rows at the sample's v, interpolated once (a column reads 2 texels per LUT, not 4). The same reads and weights as
 * `evaluateSkyViewRadiance`; only the bilinear's lerp ORDER differs (v then u), so the bake matches the per-texel oracle
 * to rounding — within one float32 ulp of what the texture stores (`sky-lut-bake-identity` pins ≤ 1e-6 relative).
 */
interface SkyViewRow {
  /** false = the ray never left the camera (evaluateSkyViewRadiance's early zero). */
  live: boolean;
  dt: number;
  /** The ray's length (the ground hit or the top of the atmosphere). */
  tMax: number;
  /** Per sample s: its distance t and radius r; ×3 channels: σ_s Rayleigh, σ_s Mie, the throughput BEFORE s. */
  t: Float64Array; r: Float64Array;
  sigSr: Float64Array; sigSm: Float64Array; tr: Float64Array;
  hitGround: boolean; trEndX: number; trEndY: number; trEndZ: number;
  /**
   * THE LOCAL SUN's per-sample constants (transmittanceUV / sampleTransmittanceLut / earthShadowCpu /
   * multiScatterUV / sampleMultiScatterLut, split at the column): r·r, r·r − Rg², d_min, d_max − d_min; the two LUTs'
   * clamped row pair and row weight.
   */
  rr: Float64Array; cG: Float64Array; dMin: Float64Array; dSpan: Float64Array;
  /**
   * Each LUT's two rows at the sample's radius, ALREADY interpolated by the row weight (×3 channels), over just the
   * texels the row's columns can reach: a column then reads 2 texels per LUT instead of 4. `tBase[s]` indexes texel x
   * of sample s's transmittance row as tBase + 3x (the same for Ψ with mBase).
   */
  tRow: Float64Array; tBase: Int32Array; mRow: Float64Array; mBase: Int32Array;
}

function newSkyViewRow(steps: number): SkyViewRow {
  const n = steps * 3;
  return {
    live: false, dt: 0, tMax: 0,
    t: new Float64Array(steps), r: new Float64Array(steps),
    sigSr: new Float64Array(n), sigSm: new Float64Array(n), tr: new Float64Array(n),
    hitGround: false, trEndX: 1, trEndY: 1, trEndZ: 1,
    rr: new Float64Array(steps), cG: new Float64Array(steps), dMin: new Float64Array(steps), dSpan: new Float64Array(steps),
    tRow: new Float64Array(0), tBase: new Int32Array(steps), mRow: new Float64Array(0), mBase: new Int32Array(steps),
  };
}

function clampIdx(x: number, lo: number, hi: number): number { return x < lo ? lo : (x > hi ? hi : x); }

/**
 * Texels x0..x1 of a LUT's two rows at v, interpolated by the row weight exactly as sampleTransmittanceLut's half-texel
 * ClampToEdge read would, written at `cur` in the row's buffer; returns the base so texel x sits at base + 3x.
 */
function fillLerpedRow(row: SkyViewRow, trans: boolean, cur: number, data: Float32Array, w: number, h: number, v: number,
  x0: number, x1: number): number {
  const out = trans ? row.tRow : row.mRow;
  const fy = v * h - 0.5;
  const y0 = Math.floor(fy);
  const ty = fy - y0;
  const r0 = clampIdx(y0, 0, h - 1) * w, r1 = clampIdx(y0 + 1, 0, h - 1) * w;
  let o = cur;
  for (let x = x0; x <= x1; x++, o += 3) {
    const a = (r0 + x) * 4, b = (r1 + x) * 4;
    out[o] = lerp(data[a], data[b], ty);
    out[o + 1] = lerp(data[a + 1], data[b + 1], ty);
    out[o + 2] = lerp(data[a + 2], data[b + 2], ty);
  }
  return cur - x0 * 3;
}

/** Fill `row` for one view-zenith cosine — evaluateSkyViewRadiance's march, minus everything that reads the sun. */
function skyViewRowPrepare(ctx: SkyViewContext, cosViewZenith: number, steps: number, row: SkyViewRow): void {
  const { cfg } = ctx;
  const r0 = ctx.viewHeight;
  const mu = cosViewZenith;
  const tTop = rayAtmosphereIntersect(r0, mu, cfg.topRadius);
  const tGround = rayNearIntersect(r0, mu, cfg.bottomRadius);
  let tMax = tTop;
  let hitGround = false;
  if (tGround > 0 && tGround < tTop) {
    tMax = tGround;
    hitGround = true;
  }
  row.live = !(tMax <= 0);
  if (!row.live) return;
  const dt = tMax / steps;
  row.dt = dt;
  row.tMax = tMax;
  // transmittanceUV's constants, computed the way it computes them.
  const Rg = cfg.bottomRadius;
  const Rt = cfg.topRadius;
  const H = Math.sqrt(Rt * Rt - Rg * Rg);
  const tW = ctx.transmittanceWidth, tH = ctx.transmittanceHeight, mS = ctx.multiScatterSize;
  if (row.tRow.length < steps * tW * 3) row.tRow = new Float64Array(steps * tW * 3);
  if (row.mRow.length < steps * mS * 3) row.mRow = new Float64Array(steps * mS * 3);
  let tCur = 0, mCur = 0;
  let trX = 1, trY = 1, trZ = 1;
  for (let s = 0; s < steps; s++) {
    const t = (s + 0.5) * dt;
    const rAtSample = Math.sqrt(r0 * r0 + 2.0 * r0 * mu * t + t * t);
    const altitudeKm = Math.max(0, rAtSample - cfg.bottomRadius);
    const d = computeDensities(cfg, altitudeKm);
    const k = s * 3;
    row.t[s] = t; row.r[s] = rAtSample;
    row.sigSr[k] = cfg.rayleighScattering.x * d.rayleigh;
    row.sigSr[k + 1] = cfg.rayleighScattering.y * d.rayleigh;
    row.sigSr[k + 2] = cfg.rayleighScattering.z * d.rayleigh;
    row.sigSm[k] = cfg.mieScattering.x * d.mie;
    row.sigSm[k + 1] = cfg.mieScattering.y * d.mie;
    row.sigSm[k + 2] = cfg.mieScattering.z * d.mie;
    const extX = cfg.rayleighScattering.x * d.rayleigh
      + cfg.mieExtinction.x * d.mie + cfg.ozoneExtinction.x * d.ozone;
    const extY = cfg.rayleighScattering.y * d.rayleigh
      + cfg.mieExtinction.y * d.mie + cfg.ozoneExtinction.y * d.ozone;
    const extZ = cfg.rayleighScattering.z * d.rayleigh
      + cfg.mieExtinction.z * d.mie + cfg.ozoneExtinction.z * d.ozone;
    row.tr[k] = trX; row.tr[k + 1] = trY; row.tr[k + 2] = trZ;
    trX *= Math.exp(-extX * dt);
    trY *= Math.exp(-extY * dt);
    trZ *= Math.exp(-extZ * dt);
    // THE LOCAL SUN's radius-only constants — transmittanceUV (rho, x_r, d_min, d_max), the shadow's c, the rows.
    const rr = rAtSample * rAtSample;
    const rho = Math.sqrt(Math.max(0, rAtSample * rAtSample - Rg * Rg));
    const dMin = Rt - rAtSample;
    row.rr[s] = rr;
    row.cG[s] = rAtSample * rAtSample - Rg * Rg;
    row.dMin[s] = dMin;
    row.dSpan[s] = (rho + H) - dMin;
    // The sun's cosine here spans [(r0·μs − t)/r, (r0·μs + t)/r] over the row's columns (ν ∈ [−1, 1]).
    const muLo = Math.max(-1, (r0 * ctx.cosSunZenith - t) / rAtSample);
    const muHi = Math.min(1, (r0 * ctx.cosSunZenith + t) / rAtSample);
    // Transmittance: u falls as μ rises (d shrinks), so the reachable texels are [x(u(μHi)), x(u(μLo)) + 1] (+1 margin).
    const uOf = (m: number): number => {
      const dTop = Math.max(0, -rAtSample * m + Math.sqrt(Math.max(0, rr * (m * m - 1.0) + Rt * Rt)));
      const span = (rho + H) - dMin;
      return clamp01(span !== 0 ? (dTop - dMin) / span : 0);
    };
    const tx0 = clampIdx(Math.floor(uOf(muHi) * tW - 0.5) - 1, 0, tW - 1);
    const tx1 = clampIdx(Math.floor(uOf(muLo) * tW - 0.5) + 2, 0, tW - 1);
    row.tBase[s] = fillLerpedRow(row, true, tCur, ctx.transmittanceData, tW, tH, clamp01(rho / H), tx0, tx1);
    tCur += (tx1 - tx0 + 1) * 3;
    const mx0 = clampIdx(Math.floor(clamp01(muLo * 0.5 + 0.5) * mS - 0.5) - 1, 0, mS - 1);
    const mx1 = clampIdx(Math.floor(clamp01(muHi * 0.5 + 0.5) * mS - 0.5) + 2, 0, mS - 1);
    row.mBase[s] = fillLerpedRow(row, false, mCur, ctx.multiScatterData, mS, mS, clamp01((rAtSample - Rg) / (Rt - Rg)), mx0, mx1);
    mCur += (mx1 - mx0 + 1) * 3;
  }
  row.hitGround = hitGround;
  row.trEndX = trX; row.trEndY = trY; row.trEndZ = trZ;
}

/** One column of a prepared row: the phase-weighted march with THE LOCAL SUN's lookups, the ground bounce — into _col.
 *  Its own function so the engine optimizes the hot loop (inlined in the bake's nested loops it ran slower than per-texel). */
const _col = { x: 0, y: 0, z: 0 };
function columnRadiance(ctx: SkyViewContext, row: SkyViewRow, steps: number, cosLightView: number): void {
  const { cfg } = ctx;
  const r0 = ctx.viewHeight;
  const Rt2 = cfg.topRadius * cfg.topRadius;
  const tW = ctx.transmittanceWidth, mS = ctx.multiScatterSize;
  const tRow = row.tRow, mRow = row.mRow;
  const phaseR = rayleighPhase(cosLightView);
  const phaseM = cornetteShanksPhase(cosLightView, ctx.cfg.miePhaseG);
  const dt = row.dt;
  let lx = 0, ly = 0, lz = 0;
  for (let s = 0; s < steps; s++) {
    const k = s * 3;
    const rAtSample = row.r[s];
    // THE LOCAL SUN — the same three lookups, the same expressions, the same order as evaluateSkyViewRadiance.
    const muS = localSunCos(r0, ctx.cosSunZenith, row.t[s], cosLightView, rAtSample);
    // earthShadowCpu: rayNearIntersect(r, μs, Rg) > 0 ⇔ the discriminant holds and the far root is ahead (t1 ≥ t0).
    const b = rAtSample * muS;
    const disc = b * b - row.cG[s];
    const shadow = disc >= 0 && -b + Math.sqrt(disc) > 0 ? 0 : 1;
    let stX = 0, stY = 0, stZ = 0;
    if (shadow !== 0) {
      // transmittanceUV's u, then sampleTransmittanceLut's bilinear read on the sample's hoisted rows.
      const dTop = Math.max(0, -rAtSample * muS + Math.sqrt(Math.max(0, row.rr[s] * (muS * muS - 1.0) + Rt2)));
      const span = row.dSpan[s];
      const fx = clamp01(span !== 0 ? (dTop - row.dMin[s]) / span : 0) * tW - 0.5;
      const x0 = Math.floor(fx);
      const tx = fx - x0;
      const i0 = row.tBase[s] + clampIdx(x0, 0, tW - 1) * 3, i1 = row.tBase[s] + clampIdx(x0 + 1, 0, tW - 1) * 3;
      stX = lerp(tRow[i0], tRow[i1], tx) * shadow;
      stY = lerp(tRow[i0 + 1], tRow[i1 + 1], tx) * shadow;
      stZ = lerp(tRow[i0 + 2], tRow[i1 + 2], tx) * shadow;
    }
    // multiScatterUV's u, then the bilinear read on the hoisted rows.
    const fxM = clamp01(muS * 0.5 + 0.5) * mS - 0.5;
    const xM = Math.floor(fxM);
    const txM = fxM - xM;
    const j0 = row.mBase[s] + clampIdx(xM, 0, mS - 1) * 3, j1 = row.mBase[s] + clampIdx(xM + 1, 0, mS - 1) * 3;
    const psiX = lerp(mRow[j0], mRow[j1], txM);
    const psiY = lerp(mRow[j0 + 1], mRow[j1 + 1], txM);
    const psiZ = lerp(mRow[j0 + 2], mRow[j1 + 2], txM);
    const sigSrX = row.sigSr[k], sigSrY = row.sigSr[k + 1], sigSrZ = row.sigSr[k + 2];
    const sigSmX = row.sigSm[k], sigSmY = row.sigSm[k + 1], sigSmZ = row.sigSm[k + 2];
    const inX = (sigSrX * phaseR + sigSmX * phaseM) * stX + (sigSrX + sigSmX) * psiX;
    const inY = (sigSrY * phaseR + sigSmY * phaseM) * stY + (sigSrY + sigSmY) * psiY;
    const inZ = (sigSrZ * phaseR + sigSmZ * phaseM) * stZ + (sigSrZ + sigSmZ) * psiZ;
    lx += row.tr[k] * inX * dt;
    ly += row.tr[k + 1] * inY * dt;
    lz += row.tr[k + 2] * inZ * dt;
  }
  if (row.hitGround) {
    const groundR = cfg.bottomRadius;
    const muG = localSunCos(r0, ctx.cosSunZenith, row.tMax, cosLightView, groundR);
    const tUV = transmittanceUV(cfg, groundR, muG);
    const sunT = sampleTransmittanceLut(
      ctx.transmittanceData, ctx.transmittanceWidth, ctx.transmittanceHeight,
      tUV.u, tUV.v,
    );
    const nDotL = Math.max(0, muG);
    const a = cfg.groundAlbedo;
    lx += row.trEndX * sunT.x * a.x * nDotL / Math.PI;
    ly += row.trEndY * sunT.y * a.y * nDotL / Math.PI;
    lz += row.trEndZ * sunT.z * a.z * nDotL / Math.PI;
  }
  _col.x = lx; _col.y = ly; _col.z = lz;
}

/** Bake the sky-view LUT (192×108 Float RGBA). */
export function bakeSkyViewLut(
  ctx: SkyViewContext,
  width = SKYVIEW_LUT_WIDTH,
  height = SKYVIEW_LUT_HEIGHT,
): SkyViewLutResult {
  const data = new Float32Array(width * height * 4);
  const steps = SKYVIEW_MARCH_STEPS;
  const row = newSkyViewRow(steps);
  for (let y = 0; y < height; y++) {
    const v = (y + 0.5) / height;
    let rowMu = Number.NaN;
    for (let x = 0; x < width; x++) {
      const u = (x + 0.5) / width;
      const { cosViewZenith, cosLightView } = skyViewUVInverse(ctx.cfg, ctx.viewHeight, u, v);
      const i = (y * width + x) * 4;
      // The row's samples hold for every texel whose zenith cosine is the row's (all of them under the shipped
      // parameterization); a texel that differed would march on its own — the bake can never drift from the oracle.
      if (x === 0) { rowMu = cosViewZenith; skyViewRowPrepare(ctx, cosViewZenith, steps, row); }
      else if (!Object.is(cosViewZenith, rowMu)) {
        const rad = evaluateSkyViewRadiance(ctx, cosViewZenith, cosLightView);
        data[i + 0] = rad.x; data[i + 1] = rad.y; data[i + 2] = rad.z; data[i + 3] = 1.0;
        continue;
      }
      if (!row.live) { data[i + 0] = 0; data[i + 1] = 0; data[i + 2] = 0; data[i + 3] = 1.0; continue; }
      columnRadiance(ctx, row, steps, cosLightView);
      const lx = _col.x, ly = _col.y, lz = _col.z;
      data[i + 0] = lx * ctx.sunIlluminance;
      data[i + 1] = ly * ctx.sunIlluminance;
      data[i + 2] = lz * ctx.sunIlluminance;
      data[i + 3] = 1.0;
    }
  }
  const texture = makeFloatTexture(data, width, height, 'SkySkyViewLUT');
  return { data, width, height, texture };
}

/** Bilinear sample of a baked sky-view LUT (mirrors GPU LinearFilter). */
export function sampleSkyViewLut(
  data: Float32Array, width: number, height: number, u: number, v: number,
): Vec3 {
  return sampleTransmittanceLut(data, width, height, clamp01(u), clamp01(v));
}
